/**
 * Header chrome: only rendered when a user has 2+ customer memberships.
 *
 * - Trigger is a compact icon-avatar button (workspace initials) + chevron,
 *   no text - saves header real estate on mobile and desktop alike.
 * - Dropdown opens a search field and a scrollable list capped to ~7 rows
 *   of visible height before scrolling. Each row shows the workspace name
 *   followed by the role (e.g. "Acme Inc. · super_admin"), an active tick
 *   and an owner crown.
 * - Hidden during impersonation (ImpersonationBanner is the exit path).
 */

import { useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Crown, Loader2, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/Modal'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useAuth } from '@/auth/useAuth'
import {
  useCreateWorkspace,
  useMemberships,
  useSwitchTenant,
} from '@/auth/useMemberships'
import { cn } from '@/lib/cn'

// How many items show without scrolling. Each row is ~52px tall
// (14px * 2 line-heights + 10px padding + divider margins).
const VISIBLE_ROWS = 7
const ROW_HEIGHT_PX = 52

function initialsFor(name: string): string {
  if (!name) return '?'
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
  return letters || name.slice(0, 2).toUpperCase()
}

export function TenantSwitcher() {
  const { user, isImpersonating } = useAuth()
  const membershipsQ = useMemberships()
  const switchMut = useSwitchTenant()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement | null>(null)

  const memberships = membershipsQ.data ?? []
  const activeTenantId = user?.tenantId ?? null
  const active = useMemo(
    () => memberships.find((m) => m.tenantId === activeTenantId) ?? null,
    [memberships, activeTenantId]
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return memberships
    return memberships.filter((m) =>
      m.tenantName.toLowerCase().includes(q) ||
      m.roleName.toLowerCase().includes(q)
    )
  }, [memberships, query])

  if (isImpersonating) return null
  if (memberships.length <= 1) return null

  const activeName = active?.tenantName ?? 'Workspace'

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setQuery('')
          if (next) {
            // Focus search right after the popover paints.
            setTimeout(() => searchRef.current?.focus(), 30)
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'group h-9 gap-1.5 rounded-pill border px-1 pr-2 shadow-sm transition',
              'hover:border-primary/40 hover:bg-accent hover:shadow',
              'focus-visible:ring-2 focus-visible:ring-ring',
              open && 'border-primary/60 bg-accent ring-2 ring-primary/20',
            )}
            aria-label={`Switch workspace - currently ${activeName}`}
            aria-expanded={open}
            aria-haspopup="listbox"
            title={`Switch workspace - ${activeName}`}
          >
            <span className="flex size-7 items-center justify-center rounded-pill bg-primary-gradient text-[10px] font-semibold uppercase leading-none tracking-wide text-primary-foreground shadow-inner">
              {initialsFor(activeName)}
            </span>
            <ChevronDown
              className={cn(
                'size-4 text-muted-foreground transition-transform duration-150',
                'group-hover:text-foreground',
                open && 'rotate-180 text-foreground'
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={8}
          className="w-[min(92vw,22rem)] p-0"
        >
          <div className="border-b px-3 py-2.5">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Workspaces
            </div>
            <div className="mt-1.5 flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5">
              <Search className="size-3.5 text-muted-foreground" />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
                placeholder="Search workspaces…"
                className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                aria-label="Search workspaces"
              />
            </div>
          </div>

          <div
            className="overflow-y-auto overscroll-contain"
            style={{ maxHeight: `${VISIBLE_ROWS * ROW_HEIGHT_PX}px` }}
          >
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No workspaces match &ldquo;{query}&rdquo;.
              </div>
            ) : (
              <ul role="listbox" className="py-1">
                {filtered.map((m) => {
                  const isActive = m.tenantId === activeTenantId
                  const busy = switchMut.isPending && switchMut.variables === m.tenantId
                  return (
                    <li key={m.tenantId}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        disabled={isActive || switchMut.isPending}
                        onClick={() => {
                          if (isActive) return
                          switchMut.mutate(m.tenantId)
                          setOpen(false)
                        }}
                        className={cn(
                          'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition',
                          'hover:bg-accent focus:bg-accent focus:outline-none',
                          isActive && 'bg-accent/60',
                          switchMut.isPending && !isActive && 'opacity-60',
                        )}
                      >
                        <span
                          className={cn(
                            'flex size-8 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold uppercase leading-none tracking-wide',
                            isActive
                              ? 'bg-primary-gradient text-primary-foreground'
                              : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {initialsFor(m.tenantName)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-medium">
                              {m.tenantName}
                            </span>
                            {m.isOwner ? (
                              <Crown className="size-3 shrink-0 text-amber-500" aria-label="Owner" />
                            ) : null}
                          </span>
                          <span className="block truncate text-xs capitalize text-muted-foreground">
                            {m.roleName.replace(/_/g, ' ')}
                            {m.tenantPlan ? ` · ${m.tenantPlan}` : ''}
                          </span>
                        </span>
                        <span className="w-4 shrink-0 text-primary">
                          {busy ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : isActive ? (
                            <Check className="size-4" />
                          ) : null}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="border-t p-1.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setCreateOpen(true)
              }}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-sm font-medium text-primary transition hover:bg-accent focus:bg-accent focus:outline-none"
            >
              <Plus className="size-4" /> Create new workspace
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <CreateWorkspaceModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  )
}

/**
 * Small standalone modal so the Profile page and picker can reuse it.
 * Creates a new free-tier tenant where the caller is super_admin. Does
 * NOT auto-switch - toast + memberships refetch only.
 */
export function CreateWorkspaceModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createMut = useCreateWorkspace()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setName('')
    setError(null)
  }

  const close = () => {
    reset()
    onClose()
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Workspace name is required.')
      return
    }
    try {
      await createMut.mutateAsync(trimmed)
      close()
    } catch (err) {
      const e = err as { body?: { error?: string }; message?: string }
      setError(e.body?.error || e.message || 'Failed to create workspace.')
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => { if (!next) close() }}
      title="Create new workspace"
      description="Start a fresh workspace - separate data, plan, billing, and team. You'll be the owner."
    >
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm font-medium" htmlFor="ws-name">Workspace name</label>
        <input
          id="ws-name"
          className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          placeholder="Acme Inc."
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          maxLength={120}
          disabled={createMut.isPending}
        />
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={close} disabled={createMut.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMut.isPending}>
            {createMut.isPending ? (<><Loader2 className="size-4 animate-spin" /> Creating…</>) : 'Create workspace'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

